import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Upload, FileText, ShieldCheck, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ConfirmDelete from "@/components/ConfirmDelete";

export default function Compliance() {
  const { branch, user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ status: "Pending" });
  const [uploadingId, setUploadingId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/compliance");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((c) => branch === "All" || c.applicable_to === "All" || c.applicable_to === branch);

  const save = async () => {
    try {
      const { id, ...body } = form;
      if (id) {
        await api.patch(`/compliance/${id}`, body);
        toast.success("Compliance item updated");
      } else {
        await api.post("/compliance", body);
        toast.success("Compliance item added");
      }
      setOpen(false);
      setForm({ status: "Pending" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const canManage = ["super_admin", "hr_manager"].includes(user?.role);

  const openEdit = (c) => {
    setForm({
      id: c.id, item: c.item, applicable_to: c.applicable_to, owner: c.owner || "",
      due_date: c.due_date?.slice(0, 10) || "", status: c.status || "Pending",
      remarks: c.remarks || "",
    });
    setOpen(true);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/compliance/${id}`);
      toast.success("Compliance item deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const upload = async (e, cid) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(cid);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/files/upload?entity=compliance&entity_id=${cid}&doc_type=Proof`, fd);
      toast.success("Proof uploaded — status auto-set to Done");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingId(null);
      e.target.value = "";
    }
  };

  const statusCls = (s) => ({ Done: "rag-green", Pending: "rag-amber", Overdue: "rag-red" }[s] || "rag-amber");

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow">HR Compliance</div>
          <h1 className="font-heading text-3xl font-bold text-[var(--yc-navy)] tracking-tight mt-1">Tasks &amp; Audit Tracker</h1>
          <p className="text-slate-600 text-sm mt-1">Status auto-marks &ldquo;Done&rdquo; once proof of completion is uploaded.</p>
        </div>
        {canManage && (
          <Button onClick={() => { setForm({ status: "Pending" }); setOpen(true); }} className="rounded-full bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="add-compliance-button">
            <Plus className="h-4 w-4 mr-1" /> Add item
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 bg-slate-50">
                <th className="data-cell">Item</th>
                <th className="data-cell">Applies to</th>
                <th className="data-cell">Owner</th>
                <th className="data-cell">Due</th>
                <th className="data-cell">Status</th>
                <th className="data-cell">Proof</th>
                <th className="data-cell">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="data-cell text-center text-slate-500 py-8">No compliance items yet.</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 data-row" data-testid={`compliance-row-${c.id}`}>
                  <td className="data-cell">
                    <div className="font-medium text-[var(--yc-navy)] flex items-center gap-2">
                      <ShieldCheck className="h-3.5 w-3.5 text-slate-400" /> {c.item}
                    </div>
                    {c.remarks && <div className="text-xs text-slate-500 mt-0.5">{c.remarks}</div>}
                  </td>
                  <td className="data-cell text-slate-600">{c.applicable_to}</td>
                  <td className="data-cell text-slate-600">{c.owner}</td>
                  <td className="data-cell text-slate-500">{c.due_date?.slice(0, 10)}</td>
                  <td className="data-cell">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${statusCls(c.status)}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="data-cell">
                    {c.has_proof && c.proof_file_id ? (
                      <a href={`${api.defaults.baseURL}/files/${c.proof_file_id}/download?auth=${localStorage.getItem("yc_token")}`} target="_blank" rel="noreferrer" className="text-xs text-[var(--yc-emerald)] hover:underline inline-flex items-center gap-1">
                        <FileText className="h-3.5 w-3.5" /> View proof
                      </a>
                    ) : canManage ? (
                      <label className="inline-flex items-center gap-1 text-xs text-slate-600 border border-dashed border-slate-300 rounded-md px-2 py-1 cursor-pointer hover:bg-slate-50">
                        <Upload className="h-3.5 w-3.5" />
                        {uploadingId === c.id ? "Uploading…" : "Upload"}
                        <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => upload(e, c.id)} data-testid={`compliance-upload-${c.id}`} />
                      </label>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="data-cell">
                    <div className="flex items-center gap-1">
                      {canManage && (
                        <>
                          <button type="button" onClick={() => openEdit(c)} className="p-1.5 rounded-md text-slate-400 hover:text-[var(--yc-navy)] hover:bg-slate-100 transition-colors" title="Edit" data-testid={`edit-compliance-${c.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <ConfirmDelete onConfirm={() => remove(c.id)} label={`compliance item "${c.item}"`} testId={`delete-compliance-${c.id}`} />
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{form.id ? "Edit compliance item" : "Add compliance item"}</DialogTitle>
            <DialogDescription>Track audit and statutory items by branch.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label className="label-eyebrow">Item</Label><Input value={form.item || ""} onChange={(e) => setForm({...form, item: e.target.value})} className="mt-1.5" data-testid="compliance-item-input" /></div>
            <div>
              <Label className="label-eyebrow">Applies to</Label>
              <Select value={form.applicable_to || ""} onValueChange={(v) => setForm({...form, applicable_to: v})}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Scope" /></SelectTrigger>
                <SelectContent>
                  {["All", "Sarita Vihar", "Mathura", "Jaipur", "Institute"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="label-eyebrow">Owner</Label><Input value={form.owner || ""} onChange={(e) => setForm({...form, owner: e.target.value})} className="mt-1.5" /></div>
            <div><Label className="label-eyebrow">Due date</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({...form, due_date: e.target.value})} className="mt-1.5" /></div>
            <div>
              <Label className="label-eyebrow">Status</Label>
              <Select value={form.status || "Pending"} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["Pending", "Done", "Overdue"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="label-eyebrow">Remarks</Label><Input value={form.remarks || ""} onChange={(e) => setForm({...form, remarks: e.target.value})} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="save-compliance-button">{form.id ? "Save changes" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
